import React, { ReactNode } from 'react';
import { buildQuery, usePageQuery, useShareUrls } from '../../hooks';
import { useTranslation } from 'react-i18next';
import { ShareContextProvider } from '../../context/ShareContext';
import { gql } from '@apollo/client';
import { InMemoryShareProviderFragment } from '../../../types/__generated__';
import { filterByTypename } from '../../utils/graphql';

const FRAGMENT = buildQuery(gql`
  fragment InMemoryShareProvider on Page {
    inMemory {
      firstNameOfDeceased
      lastNameOfDeceased
    }
    relationships {
      beneficiaries(first: 1) {
        nodes {
          ... on Charity {
            id
            name
          }
        }
      }
    }
  }
`);

interface InMemoryShareProps {
  children: ReactNode;
}

export function InMemoryShareProvider({ children }: InMemoryShareProps) {
  const { data } = usePageQuery<InMemoryShareProviderFragment>(FRAGMENT);
  const { t } = useTranslation('common', { useSuspense: false });

  const { firstNameOfDeceased, lastNameOfDeceased } = data?.inMemory || {};

  const shareName =
    firstNameOfDeceased && lastNameOfDeceased
      ? `${firstNameOfDeceased} ${lastNameOfDeceased}`
      : t('inMemory.cta.shareMessages.aFriend');

  const charitySummary = () => {
    const beneficiaryNodes = data?.relationships?.beneficiaries.nodes;
    const charities = beneficiaryNodes
      ? filterByTypename(beneficiaryNodes, 'Charity')
      : null;

    if (!charities || charities.length === 0) {
      return t('inMemory.cta.shareMessages.noCharity');
    }

    if (charities.length === 1) {
      return t('inMemory.cta.shareMessages.oneCharity', {
        charityName: charities[0].name,
      });
    }

    return t('inMemory.cta.shareMessages.multipleCharities', {
      charityCount: charities.length,
    });
  };

  const socialMessage = t('inMemory.cta.shareMessages.summary', {
    shareName,
    charitySummary: charitySummary(),
  });

  const emailMapper = (shareUrl: string) => ({
    subject: t('inMemory.cta.shareMessages.emailSubject', { shareName }),
    body: t('inMemory.cta.shareMessages.emailBody', {
      socialMessage,
      shareUrl,
    }),
  });

  const commonMapper = () => socialMessage;

  const shareItems = useShareUrls({
    email: emailMapper,
    sms: commonMapper,
    twitter: commonMapper,
    whatsApp: commonMapper,
  });

  return (
    <ShareContextProvider value={{ shareItems }}>
      {children}
    </ShareContextProvider>
  );
}
